import { Box, Typography } from "@mui/material";
import { GetSubmissionResponsePortal } from "@rubi/api-generated";
import { colors } from "@rubi/ui";

import { extractDate, extractLocation, formatDate } from "./submissionFields";

export const InfoRow = ({
  label,
  value,
  stacked = false,
}: {
  label: string;
  value: string;
  /** Render the label above the value (for narrow containers like the panel). */
  stacked?: boolean;
}) => {
  if (stacked) {
    return (
      <Box sx={{ mb: 1.25 }}>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontWeight: 700,
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          {label}
        </Typography>
        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
          {value}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", mb: 0.5 }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, minWidth: 140, flexShrink: 0 }}
      >
        {label}:
      </Typography>
      <Typography
        variant="body2"
        sx={{ minWidth: 0, overflowWrap: "anywhere" }}
      >
        {value}
      </Typography>
    </Box>
  );
};

interface SubmissionSummaryCardProps {
  submission: GetSubmissionResponsePortal | null;
  attachmentUrl: string | null;
  attachmentFilename: string | null;
  /** Match metadata, shown only for matched submissions (not the source). */
  matchNotes?: string | null;
  matchedOn?: string | null;
  imageWidth?: number;
  /**
   * Vertical layout for narrow containers (the map side panel): image on top,
   * labels above values. Defaults to the side-by-side report layout.
   */
  stacked?: boolean;
}

/**
 * The attachment-thumbnail + details layout shared by the match report's
 * source/matched sections and the map side panel.
 */
const SubmissionSummaryCard = ({
  submission,
  attachmentUrl,
  attachmentFilename,
  matchNotes,
  matchedOn,
  imageWidth = 200,
  stacked = false,
}: SubmissionSummaryCardProps) => {
  const submittedBy =
    submission?.first_name || submission?.last_name
      ? `${submission?.first_name || ""} ${submission?.last_name || ""}`.trim()
      : "N/A";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: stacked ? "column" : "row",
        gap: stacked ? 2 : 3,
        alignItems: stacked ? "stretch" : "flex-start",
      }}
    >
      {attachmentUrl && (
        <Box
          sx={{
            width: stacked ? "100%" : imageWidth,
            flexShrink: 0,
            borderRadius: 1,
            overflow: "hidden",
            border: "1px solid var(--divider)",
            display: "flex",
            justifyContent: "center",
            backgroundColor: stacked ? colors.primary.contrastText : undefined,
          }}
        >
          <img
            src={attachmentUrl}
            alt={attachmentFilename || "Attachment"}
            style={
              stacked
                ? { maxWidth: "100%", maxHeight: 240, objectFit: "contain" }
                : { width: "100%", height: "auto" }
            }
          />
        </Box>
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <InfoRow
          stacked={stacked}
          label="Report Number"
          value={
            submission?.tracking_number || submission?.incident_number || "N/A"
          }
        />
        <InfoRow
          stacked={stacked}
          label="Report Type"
          value={submission?.survey_name || "N/A"}
        />
        <InfoRow
          stacked={stacked}
          label="Date Submitted"
          value={formatDate(submission?.time_submitted)}
        />
        {extractDate(submission) && (
          <InfoRow
            stacked={stacked}
            label="Date of Incident"
            value={extractDate(submission) || "N/A"}
          />
        )}
        <InfoRow
          stacked={stacked}
          label="Location"
          value={extractLocation(submission)}
        />
        <InfoRow stacked={stacked} label="Submitted By" value={submittedBy} />
        {submission?.email && (
          <InfoRow stacked={stacked} label="Email" value={submission.email} />
        )}
        {submission?.phone_number && (
          <InfoRow
            stacked={stacked}
            label="Phone"
            value={submission.phone_number}
          />
        )}
        {submission?.submitter_agency && (
          <InfoRow
            stacked={stacked}
            label="Agency"
            value={submission.submitter_agency}
          />
        )}
        {matchNotes && (
          <InfoRow stacked={stacked} label="Match Notes" value={matchNotes} />
        )}
        {matchedOn && (
          <InfoRow
            stacked={stacked}
            label="Matched On"
            value={formatDate(matchedOn)}
          />
        )}
      </Box>
    </Box>
  );
};

export default SubmissionSummaryCard;
